import RecoverableImage from '../../Media/RecoverableImage';
import { urlMetaImageRecovery } from '../../../util/imageRecovery';
import UrlPreviewSource from '../../UrlPreviewSource';
import { getUrlDisplayTitle } from '../../../util/displayText';
import UrlPreviewReadButton from '../../UrlPreviewReadButton';
import { SAVE_GITHUB_REPOSITORY } from '../../../gql-operations/mutations/message/save-github-repository';
import { isGithubRepositoryUrl } from '../../../util/github-repository';
import { useCarouselReanalysis } from '../../../hooks/useCarouselReanalysis';
import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState } from 'react';
import Hls from 'hls.js';
import ReactPlayer from 'react-player';
import { getUrlVideoWorkspaceSource } from '../../../util/workspaceNavigation';
import { getYouTubeVideoId } from '../../../util/youtubeVideoId';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import CarouselModal from '../../../components/Modals/CarouselModal';
import { AuthContext } from '../../../util/context';
import environment from '../../../env-config';
import useUpdateMessageMutation from '../../../hooks/useUpdateMessageMutation';
import { PRIMARY_COLOR, RED, PRIMARY_COLOR_1, PALE_WHITE_1, FILE_BLUE } from '../../../appearance/Colors';
import { Image, Transformation } from 'cloudinary-react';
import { IoIosClose } from 'react-icons/io';
import { FiBookOpen, FiCopy, FiDownload, FiEdit3, FiFileText, FiImage, FiMessageSquare, FiMoreVertical, FiRefreshCw, FiTool } from 'react-icons/fi';
import { LuScanText } from 'react-icons/lu';
import useCheckValidity from '../../../util/useCheckValidity';
import Markdown from 'markdown-to-jsx';

import ImageNAIcon from '../../../appearance/ImageNAIcon';
import customAnalytics from '../../../util/custom-analytics';
import domain_from_url from '../../../util/domain-from-url';
import {
    getUniqueMediaSources,
    getUrlMetaImageSources,
    getUrlMetaDirectImageUrl,
    getUrlMetaDirectS3VideoSource,
    getUrlMetaVideoSource,
    isUrlMetaVideo,
    decodeProviderMediaUrl,
    getUrlMetaOriginalVideoUrl
} from '../../../util/url-meta-media';
import MediaToolsPanelContainer from './MediaToolsPanelContainer';
import UrlTranscriptButton from '../../UrlTranscriptButton';
import UrlMetaQuotedPreview from '../../UrlMetaQuotedPreview';
import ArticleReaderModal, { getReadableArticleContent } from '../../ArticleReaderModal';
import { LAYOUT } from '../../Messages/layoutBreakpoints';
import useNotificationSystem from '../../../components/Notifications/useNotificationSystem';
import { MUTATION } from '../../../gql-operations';
import { CREATE_MESSAGE } from '../../../gql-operations/mutations/message/create-message';
import { GENERATE_URL_PDF } from '../../../gql-operations/mutations/message/generate-url-pdf';
import { GENERATE_URL_SCREENSHOT } from '../../../gql-operations/mutations/message/generate-url-screenshot';
import { DOWNLOAD_REDDIT_JSON_DUMP } from '../../../gql-operations/mutations/message/download-reddit-json-dump';
import { downloadBlobInBrowser, isTauriEnvironment, saveBlobWithTauri } from '../../../util/downloadHelpers';
import { buildZipDownloadFileName, createZipBlob } from '../../../util/createZip';
import {
    hasUrlPreviewMarkdownFormatting,
    normalizeUrlPreviewText,
    truncateUrlPreviewText,
    URL_PREVIEW_DESCRIPTION_COLLAPSE_LIMIT,
    URL_PREVIEW_TITLE_COLLAPSE_LIMIT
} from '../../../util/urlPreviewText';

const isImageLikeGalleryMedia = media => {
    const type = String(media?.type || '').trim().toLowerCase();
    return !type || type === 'image';
};

const isImageGallerySlide = slide => {
    const type = String(slide?.type || slide?.media?.type || '').trim().toLowerCase();
    return !type || type === 'image';
};

const getGallerySlidePosterUrl = (slide, fallbackImage) => {
    if (!slide) {
        return fallbackImage || null;
    }

    if (isImageLikeGalleryMedia(slide.media)) {
        return slide.media?.url || slide.image || fallbackImage || null;
    }

    return slide.media?.thumbnail || slide.image || fallbackImage || null;
};

const getGallerySlidePosterCdnUrl = slide => (
    isImageLikeGalleryMedia(slide?.media) ? slide?.media?.cdn_url || null : null
);

const getMediaExtension = blob => {
    const type = String(blob?.type || '').toLowerCase();
    if (type === 'image/png') return 'png';
    if (type === 'image/gif') return 'gif';
    if (type === 'image/webp') return 'webp';
    if (type === 'image/avif') return 'avif';
    if (type === 'image/svg+xml') return 'svg';
    if (type === 'image/bmp') return 'bmp';
    if (type === 'image/tiff') return 'tiff';
    if (type === 'image/heic' || type === 'image/heif') return 'heic';
    if (type === 'video/mp4') return 'mp4';
    if (type === 'video/webm') return 'webm';
    if (type === 'video/quicktime') return 'mov';
    if (type === 'video/x-matroska') return 'mkv';
    return 'jpg';
};

const fetchGalleryMediaBlob = async (sources, mediaType) => {
    for (const source of getUniqueMediaSources(sources)) {
        try {
            const response = await fetch(source);
            if (!response.ok) {
                continue;
            }

            const blob = await response.blob();
            if (blob?.type?.startsWith?.(`${mediaType}/`)) {
                return blob;
            }
        } catch (_error) {
            // Try the next available image source.
        }
    }

    return null;
};

// Separate Audio Component
const RedditAudioPlayer = React.forwardRef(({ audioUrl, onPlay, onPause, onSeek, onVolumeChange, onTimeUpdate }, ref) => {
    const audioRef = React.useRef(null);

    // no-op

    React.useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => onPlay && onPlay();
        const handlePause = () => onPause && onPause();
        const handleTimeUpdate = () => onTimeUpdate && onTimeUpdate(audio.currentTime);
        const handleSeek = () => onSeek && onSeek(audio.currentTime);
        const handleVolumeChange = () => onVolumeChange && onVolumeChange(audio.volume);

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('seeked', handleSeek);
        audio.addEventListener('volumechange', handleVolumeChange);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('seeked', handleSeek);
            audio.removeEventListener('volumechange', handleVolumeChange);
        };
    }, [onPlay, onPause, onSeek, onVolumeChange, onTimeUpdate]);

    const play = () => audioRef.current?.play();
    const pause = () => audioRef.current?.pause();
    const seek = (time) => {
        if (audioRef.current) audioRef.current.currentTime = time;
    };
    const setVolume = (volume) => {
        if (audioRef.current) audioRef.current.volume = volume;
    };

    // Expose methods to parent via ref
    React.useImperativeHandle(ref, () => ({
        play,
        pause,
        seek,
        setVolume,
        currentTime: audioRef.current?.currentTime || 0,
        duration: audioRef.current?.duration || 0
    }));

    return (
        <audio
            ref={audioRef}
            src={audioUrl}
            preload="metadata"
            style={{ display: 'none' }}
        />
    );
});

RedditAudioPlayer.displayName = 'RedditAudioPlayer';

const createClientRequestId = (scope = 'link-action') => {
    const normalizedScope = String(scope || 'link-action')
        .trim()
        .replace(/[^a-zA-Z0-9._:-]/g, '-')
        .slice(0, 40) || 'link-action';

    if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
        return `${normalizedScope}:${window.crypto.randomUUID()}`;
    }

    return `${normalizedScope}:${Date.now()}:${Math.random().toString(16).slice(2, 10)}`;
};

const sanitizeFilenameStem = value => String(value || '')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);

const parseUrl = value => {
    try {
        return new URL(value);
    } catch {
        return null;
    }
};

const isRedditUrl = value => {
    const parsed = parseUrl(value);
    if (!parsed) {
        return false;
    }

    const host = parsed.hostname.toLowerCase();
    return host === 'reddit.com' ||
        host === 'www.reddit.com' ||
        host === 'old.reddit.com' ||
        host === 'm.reddit.com' ||
        host === 'np.reddit.com' ||
        host === 'redd.it';
};

const sanitizeUrlMetaInput = meta => {
    if (!meta || typeof meta !== 'object') return null;

    const sanitizeQuotedUrlMetaInput = quotedMeta => {
        if (!quotedMeta || typeof quotedMeta !== 'object') return null;

        const sanitized = sanitizeUrlMetaInput(quotedMeta);
        return sanitized;
    };

    const {
        author,
        date,
        description,
        article_url,
        article_body,
        article_pull_provider,
        image,
        image_name,
        image_asset_id,
        gallery_images,
        gallery,
        media_url,
        media_name,
        media_asset_id,
        media_cdn_url,
        media_type,
        media_width,
        media_height,
        media_duration,
        media_has_audio,
        media_audio_url,
        media_audio_name,
        media_audio_asset_id,
        media_hls_url,
        logo,
        publisher,
        title,
        url,
        public_id,
        name,
        displayName,
        type,
        format,
        size,
        aspect_ratio,
        width,
        embed,
        quoted_url_meta
    } = meta;

    return {
        author,
        date,
        description,
        article_url,
        article_body,
        article_pull_provider,
        image,
        image_name,
        image_asset_id,
        gallery_images,
        gallery,
        media_url,
        media_name,
        media_asset_id,
        media_cdn_url,
        media_type,
        media_width,
        media_height,
        media_duration,
        media_has_audio,
        media_audio_url,
        media_audio_name,
        media_audio_asset_id,
        media_hls_url,
        logo,
        publisher,
        title,
        url,
        public_id,
        name,
        displayName,
        type,
        format,
        size,
        aspect_ratio,
        width,
        embed,
        quoted_url_meta: sanitizeQuotedUrlMetaInput(quoted_url_meta)
    };
};

const resolvePdfDisplayName = ({ metaData, message, pageUrl }) => {
    const titleCandidates = [metaData?.title, message?.title];

    for (const candidate of titleCandidates) {
        const stem = sanitizeFilenameStem(candidate);
        if (stem) {
            return stem.toLowerCase().endsWith('.pdf') ? stem : `${stem}.pdf`;
        }
    }

    const parsed = parseUrl(pageUrl);
    if (parsed) {
        const host = parsed.hostname.replace(/^www\./i, '');
        const pathTail = parsed.pathname.split('/').filter(Boolean).slice(-3).join('-');
        const stem = sanitizeFilenameStem([host, pathTail].filter(Boolean).join(' '));
        if (stem) {
            return `${stem}.pdf`;
        }
    }

    return `saved-page-${new Date().toISOString().slice(0, 10)}.pdf`;
};

const resolveScreenshotDisplayName = ({ metaData, message, pageUrl }) => {
    const titleCandidates = [metaData?.title, message?.title];

    for (const candidate of titleCandidates) {
        const stem = sanitizeFilenameStem(candidate);
        if (stem) {
            return stem.toLowerCase().endsWith('.png') ? stem : `${stem}.png`;
        }
    }

    const parsed = parseUrl(pageUrl);
    if (parsed) {
        const host = parsed.hostname.replace(/^www\./i, '');
        const pathTail = parsed.pathname.split('/').filter(Boolean).slice(-3).join('-');
        const stem = sanitizeFilenameStem([host, pathTail].filter(Boolean).join(' '));
        if (stem) {
            return `${stem}.png`;
        }
    }

    return `saved-page-${new Date().toISOString().slice(0, 10)}.png`;
};

const buildLinkMessageContent = ({ message, metaData, pageUrl }) => {
    const fromMessageMeta = Array.isArray(message?.urlMeta)
        ? message.urlMeta.map(sanitizeUrlMetaInput).filter(Boolean)
        : [];
    const urlMeta = fromMessageMeta.length
        ? fromMessageMeta
        : (metaData ? [sanitizeUrlMetaInput(metaData)].filter(Boolean) : []);

    return {
        title: message?.title || metaData?.title || '',
        body: message?.body || metaData?.url || pageUrl || '',
        urlMeta,
        metadata: {
            savedFrom: message?.metadata?.savedFrom || {
                type: 'WEB',
                device: 'Macintosh',
                browser: 'Chrome',
                browserVersion: '140'
            }
        }
    };
};

const hasMessageTranscriptForUrl = ({ message, index }) => {
    const transcript = message?.transcript;
    if (!transcript?.text) {
        return false;
    }

    if (
        transcript.urlMetaIndex != null &&
        Number(transcript.urlMetaIndex) !== Number(index)
    ) {
        return false;
    }

    return true;
};

// Separate Video Component
const RedditVideoPlayer = ({ videoUrl, audioUrl, poster, width, height, hasAudio, hlsUrl }) => {
    const {t: lnT} = useTranslation();

    // Derive audio URL for Reddit DASH when not provided
    const computedAudioUrl = React.useMemo(() => {
        if (audioUrl && audioUrl.length > 0) return audioUrl;
        if (videoUrl && videoUrl.includes('v.redd.it')) {
            try {
                const url = new URL(videoUrl);
                const parts = url.pathname.split('/').filter(Boolean);
                if (parts.length >= 2) {
                    const base = `${url.protocol}//${url.host}/${parts[0]}`;
                    return `${base}/DASH_AUDIO_128.mp4`;
                }
            } catch { }
        }
        return '';
    }, [lnT, videoUrl, audioUrl]);
    const videoRef = React.useRef(null);
    const audioRef = React.useRef(null);
    const shouldDefaultToMuted = typeof window === 'undefined'
        ? true
        : window.innerWidth <= LAYOUT.MOBILE_VIEWPORT_MAX;
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [volume, setVolume] = React.useState(1);
    const [showControls, setShowControls] = React.useState(false);
    const [failedHls, setFailedHls] = React.useState(false);
    const useFallbackAudio = (!hlsUrl || failedHls) && hasAudio !== false && !!computedAudioUrl;

    React.useEffect(() => setFailedHls(false), [videoUrl, hlsUrl]);

    React.useEffect(() => {
        if (videoRef.current) {
            videoRef.current.style.display = '';
        }
    }, [videoUrl, hlsUrl, failedHls]);

    // Setup HLS if available
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video || !hlsUrl || failedHls) return;
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = hlsUrl;
            return;
        }
        if (Hls.isSupported()) {
            const hls = new Hls({ enableWorker: true });
            hls.on(Hls.Events.ERROR, function (_event, data) {
                if (data.fatal) {
                    setFailedHls(true);
                    try { hls.destroy(); } catch { }
                }
            });
            hls.loadSource(hlsUrl);
            hls.attachMedia(video);
            return () => {
                try { hls.destroy(); } catch { }
            };
        }
    }, [hlsUrl, failedHls]);

    // no-op

    // Audio event handlers
    const handleAudioPlay = () => undefined;

    const handleAudioPause = () => undefined;

    const handleAudioTimeUpdate = (audioTime) => {
        // Sync video with audio if they're out of sync
        if (videoRef.current && Math.abs(videoRef.current.currentTime - audioTime) > 0.1) {
            videoRef.current.currentTime = audioTime;
        }
    };

    const handleAudioSeek = (audioTime) => {
        if (videoRef.current) {
            videoRef.current.currentTime = audioTime;
        }
    };

    const handleAudioVolumeChange = (audioVolume) => {
        if (videoRef.current) {
            videoRef.current.volume = audioVolume;
        }
        setVolume(audioVolume);
    };

    // Video event handlers
    const handleVideoTimeUpdate = event => {
        const video = event?.currentTarget || videoRef.current;
        if (!video) {
            return;
        }

        setCurrentTime(video.currentTime);
        // Sync audio with video if they're out of sync
        if ((hasAudio || computedAudioUrl) && audioRef.current && Math.abs(audioRef.current.currentTime - video.currentTime) > 0.1) {
            audioRef.current.currentTime = video.currentTime;
        }
    };

    const handleVideoPlay = event => {
        const video = event?.currentTarget || videoRef.current;
        setIsPlaying(true);
        // Start audio playback when video starts (only when no HLS)
        if (video && useFallbackAudio && audioRef.current) {
            audioRef.current.currentTime = video.currentTime;
            audioRef.current.play().catch((error) => {
                console.log('Audio play error:', error);
            });
        }
    };

    const handleVideoPause = () => {
        setIsPlaying(false);
        // Pause audio when video pauses (only when no HLS)
        if (useFallbackAudio && audioRef.current) {
            audioRef.current.pause();
        }
    };

    const handleVideoSeek = event => {
        const video = event?.currentTarget || videoRef.current;
        // Sync audio seek with video (only when no HLS)
        if (video && useFallbackAudio && audioRef.current) {
            audioRef.current.currentTime = video.currentTime;
        }
    };

    const handleVideoVolumeChange = event => {
        const video = event?.currentTarget || videoRef.current;
        if (!video) {
            return;
        }

        // Sync audio volume with video (only when no HLS)
        if (useFallbackAudio && audioRef.current) {
            audioRef.current.volume = video.volume;
        }
        setVolume(video.volume);
    };

    const handleVideoLoadedMetadata = event => {
        const video = event?.currentTarget || videoRef.current;
        if (!video) {
            return;
        }

        setDuration(video.duration);
    };

    // Setup video event listeners
    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        video.addEventListener('play', handleVideoPlay);
        video.addEventListener('pause', handleVideoPause);
        video.addEventListener('timeupdate', handleVideoTimeUpdate);
        video.addEventListener('seeked', handleVideoSeek);
        video.addEventListener('volumechange', handleVideoVolumeChange);
        video.addEventListener('loadedmetadata', handleVideoLoadedMetadata);

        return () => {
            video.removeEventListener('play', handleVideoPlay);
            video.removeEventListener('pause', handleVideoPause);
            video.removeEventListener('timeupdate', handleVideoTimeUpdate);
            video.removeEventListener('seeked', handleVideoSeek);
            video.removeEventListener('volumechange', handleVideoVolumeChange);
            video.removeEventListener('loadedmetadata', handleVideoLoadedMetadata);
        };
    }, [hasAudio, audioUrl, computedAudioUrl]);

    const togglePlay = () => {
        if (!videoRef.current) {
            return;
        }

        if (isPlaying) {
            videoRef.current.pause();
        } else {
            videoRef.current.play();
        }
    };

    const handleSeek = (e) => {
        if (!videoRef.current) {
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newTime = (clickX / rect.width) * duration;
        videoRef.current.currentTime = newTime;
        if (hasAudio && audioUrl && audioRef.current) {
            audioRef.current.currentTime = newTime;
        }
    };

    const handleVolumeChange = (e) => {
        if (!videoRef.current) {
            return;
        }

        const newVolume = parseFloat(e.target.value);
        videoRef.current.volume = newVolume;
        if (hasAudio && audioUrl && audioRef.current) {
            audioRef.current.volume = newVolume;
        }
        setVolume(newVolume);
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div
            style={{ position: 'relative' }}
        >
            {/* Video Element */}
            <video
                ref={videoRef}
                src={(hlsUrl && !failedHls) ? undefined : videoUrl}
                style={{
                    borderRadius: 8,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    maxHeight: '250px',
                    backgroundColor: '#000',
                    cursor: 'pointer'
                }}
                controls
                poster={poster}
                width={width}
                height={height}
                muted={shouldDefaultToMuted}
                referrerPolicy="no-referrer"
                onError={(e) => {
                    if (hlsUrl && !failedHls) {
                        setFailedHls(true);
                        return;
                    }
                    // Try alternative Reddit video URL if available
                    if (videoUrl && videoUrl.includes('v.redd.it')) {
                        const currentUrl = videoUrl;
                        if (currentUrl.includes('DASH_')) {
                            const hlsUrl = currentUrl.replace('DASH_', 'HLSPlaylist.m3u8').replace('.mp4', '');
                            e.target.src = hlsUrl;
                        } else if (currentUrl.includes('HLSPlaylist')) {
                            const dashUrl = currentUrl.replace('HLSPlaylist.m3u8', 'DASH_480.mp4');
                            e.target.src = dashUrl;
                        } else {
                            e.target.style.display = 'none';
                        }
                    } else {
                        if (hlsUrl && !failedHls) {
                            setFailedHls(true);
                            return;
                        }
                        e.target.style.display = 'none';
                    }
                }}
            />

            {/* Hidden Audio Component (only when no HLS) */}
            {useFallbackAudio && (
                <RedditAudioPlayer
                    ref={audioRef}
                    audioUrl={computedAudioUrl}
                    onPlay={handleAudioPlay}
                    onPause={handleAudioPause}
                    onSeek={handleAudioSeek}
                    onVolumeChange={handleAudioVolumeChange}
                    onTimeUpdate={handleAudioTimeUpdate}
                />
            )}

            {/* Audio Indicator */}
            {hasAudio === false && (
                <div style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                }}>{lnT('messages:noAudio')}</div>
            )}
        </div>
    );
};

const ignorePreviewNotification = () => undefined;

function URLPreviewPlugin(props) {
    return props.readOnly ? <URLPreviewPluginContent {...props} /> : <EditableURLPreviewPlugin {...props} />;
}

function EditableURLPreviewPlugin(props) {
    const { showDefaultNotification } = useNotificationSystem();

    const { updateURL } = useUpdateMessageMutation();
    const [refreshBookmarkPreviewMutation] = useMutation(MUTATION.REFRESH_BOOKMARK_PREVIEW);
    const [createMessageMutation] = useMutation(CREATE_MESSAGE);
    const [saveGithubRepositoryMutation] = useMutation(SAVE_GITHUB_REPOSITORY);
    const [generateUrlPdfMutation] = useMutation(GENERATE_URL_PDF);
    const [generateUrlScreenshotMutation] = useMutation(GENERATE_URL_SCREENSHOT);
    const [downloadRedditJsonDumpMutation] = useMutation(DOWNLOAD_REDDIT_JSON_DUMP);
    const [extractCarouselOcrMutation] = useMutation(MUTATION.EXTRACT_CAROUSEL_OCR);
    const [extractCarouselArtifactsMutation] = useMutation(MUTATION.EXTRACT_CAROUSEL_ARTIFACTS);
    return <URLPreviewPluginContent {...props} tools={{ showDefaultNotification, updateURL, refreshBookmarkPreviewMutation, createMessageMutation, saveGithubRepositoryMutation, generateUrlPdfMutation, generateUrlScreenshotMutation, downloadRedditJsonDumpMutation, extractCarouselOcrMutation, extractCarouselArtifactsMutation }} />;
}

function URLPreviewPluginContent({
    index,
    metaData,
    message,
    style,
    onOpenVideoWorkspace,
    hideTranscriptAction = false,
    onOpenArticleVoiceModal,
    isGeneratingArticleAudio = false,
    readOnly = false,
    canGenerate = true,
    tools = {}
}) {
    const {t: lnT} = useTranslation();

    const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
    const [isEditable, setIsEditable] = useState(false);
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [isRefreshingPreview, setIsRefreshingPreview] = useState(false);
    const [videoAnalysisState, setVideoAnalysisState] = useState({
        canAnalyze: false,
        completed: false,
        canReanalyze: false,
        processing: false
    });
    const [isSavingRepository, setIsSavingRepository] = useState(false);
    const repositorySaveInFlight = React.useRef(false);
    const [isSavingPagePdf, setIsSavingPagePdf] = useState(false);
    const [isSavingPageScreenshot, setIsSavingPageScreenshot] = useState(false);
    const [isSavingRedditJson, setIsSavingRedditJson] = useState(false);
    const [isDownloadingGallery, setIsDownloadingGallery] = useState(false);
    const [isExtractingGalleryOcr, setIsExtractingGalleryOcr] = useState(false);
    const [hasRequestedGalleryOcr, setHasRequestedGalleryOcr] = useState(false);
    const [isCopyingGalleryOcr, setIsCopyingGalleryOcr] = useState(false);
    const [isArticleReaderOpen, setIsArticleReaderOpen] = useState(false);
    const [quotedArticleReaderMeta, setQuotedArticleReaderMeta] = useState(null);
    const [extractedCarouselArticleBody, setExtractedCarouselArticleBody] = useState('');
    const [isTitleExpanded, setIsTitleExpanded] = useState(false);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [measuredPreviewWidth, setMeasuredPreviewWidth] = useState(0);
    const [loadedImageFrame, setLoadedImageFrame] = useState(null);
    const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
    const wrapperRef = React.useRef(null);
    const resizeObserverRef = React.useRef(null);
    const actionMenuRef = React.useRef(null);
    const mediaToolsRef = React.useRef(null);

    const { checkImageValidity } = useCheckValidity();
    const { showDefaultNotification = ignorePreviewNotification, updateURL, refreshBookmarkPreviewMutation, createMessageMutation, saveGithubRepositoryMutation, generateUrlPdfMutation, generateUrlScreenshotMutation, downloadRedditJsonDumpMutation, extractCarouselOcrMutation, extractCarouselArtifactsMutation } = tools;
    const isTauri = isTauriEnvironment();

    const {
        state: { holdingKeys, keys } = {}
    } = React.useContext(AuthContext) || {};
    const normalizedTitle = React.useMemo(
        () => normalizeUrlPreviewText(getUrlDisplayTitle(typeof metaData?.title === 'string' ? metaData.title : '')),
        [lnT, metaData?.title]
    );
    const normalizedDescription = React.useMemo(
        () => normalizeUrlPreviewText(typeof metaData?.description === 'string' ? metaData.description : ''),
        [lnT, metaData?.description]
    );
    const previewLinkTextColor = FILE_BLUE;
    const hasNormalizedTitle = normalizedTitle.length > 0;
    const hasNormalizedDescription = normalizedDescription.length > 0;
    const articleBody = String(extractedCarouselArticleBody || metaData?.article_body || '').trim();
    const readableArticleBody = getReadableArticleContent(articleBody, metaData?.title);
    const hasArticleBody = readableArticleBody.length > 0;
    const openArticleWorkspace = React.useCallback(articleMeta => {
        const article = articleMeta || metaData;
        if (typeof onOpenVideoWorkspace !== 'function') {
            if (articleMeta) setQuotedArticleReaderMeta(articleMeta);
            else setIsArticleReaderOpen(true);
            return;
        }

        onOpenVideoWorkspace(message, {
            type: 'article',
            title: article?.title || message?.title,
            url: article?.article_url || article?.url,
            articleBody: article?.article_body || articleBody,
            redditComments: article?.reddit_comments,
            sourcePlatform: article?.sourcePlatform,
            sourceMetrics: article?.sourceMetrics,
            sourcePlatformMeta: article?.sourcePlatformMeta,
            onCreateAudio: articleMeta || typeof onOpenArticleVoiceModal !== 'function'
                ? undefined
                : articleText => onOpenArticleVoiceModal(message, index, articleText),
            isCreateAudioDisabled: isGeneratingArticleAudio,
            isCreatingAudio: isGeneratingArticleAudio
        });
    }, [lnT, articleBody, index, isGeneratingArticleAudio, message, metaData, onOpenArticleVoiceModal, onOpenVideoWorkspace]);
    const hasPageUrl = String(metaData?.url || metaData?.media_url || '').trim().length > 0;
    const redditJsonUrl = React.useMemo(() => {
        const candidates = [
            metaData?.url,
            metaData?.article_url,
            metaData?.media_url
        ];

        return candidates.find(candidate => isRedditUrl(String(candidate || '').trim())) || '';
    }, [lnT, metaData?.article_url, metaData?.media_url, metaData?.url]);
    const canSaveRedditJson = Boolean(redditJsonUrl);
    const resolvedUrlVideo = getUrlVideoWorkspaceSource(metaData);
    const inlineYouTubeSource = getYouTubeVideoId(resolvedUrlVideo?.src) ? resolvedUrlVideo.src : '';
    const gallerySlides = React.useMemo(() => {
        if (inlineYouTubeSource) return [];
        if (Array.isArray(metaData?.gallery) && metaData.gallery.length > 0) {
            const filteredGallery = metaData.gallery.filter(slide => slide?.image || slide?.media_cdn_url || slide?.media_url);
            const hasVideoSlide = filteredGallery.some(
                slide => slide?.type === 'video' || slide?.media_cdn_url || slide?.media_url || slide?.media_hls_url
            );
            if (!hasVideoSlide && isUrlMetaVideo(metaData)) {
                return [];
            }
            return filteredGallery;
        }

        if (isUrlMetaVideo(metaData)) {
            return [];
        }

        return getUniqueMediaSources(metaData?.gallery_images || []).map(image => ({
            type: 'image',
            image,
            media_url: null
        }));
    }, [lnT, inlineYouTubeSource, metaData?.gallery, metaData?.gallery_images, metaData]);
    const hasGalleryItems = gallerySlides.length > 1;
    const hasGalleryActions = hasGalleryItems;
    const imageGallerySlides = React.useMemo(
        () => gallerySlides.filter(isImageGallerySlide),
        [lnT, gallerySlides]
    );
    const galleryOcrIsComplete = imageGallerySlides.length > 0 && imageGallerySlides.every(slide => (
        slide?.media?.ocrExtracted === true || String(slide?.media?.ocrText || slide?.ocrText || '').trim()
    ));
    const allGalleryOcrText = React.useMemo(() => {
        const seen = new Set();
        const parts = gallerySlides.reduce((result, slide, index) => {
            const text = String(slide?.media?.ocrText || slide?.ocrText || '').trim();
            if (!text || seen.has(text)) {
                return result;
            }

            seen.add(text);
            result.push(`Image ${index + 1}\n${text}`);
            return result;
        }, []);

        return parts.length > 0 ? parts.join('\n\n') : String(message?.ocrText || '').trim();
    }, [lnT, gallerySlides, message?.ocrText]);
    const galleryOcrIsPending = isExtractingGalleryOcr || (hasRequestedGalleryOcr && !galleryOcrIsComplete);
    React.useEffect(() => {
        if (galleryOcrIsComplete && hasRequestedGalleryOcr) {
            setHasRequestedGalleryOcr(false);
        }
    }, [galleryOcrIsComplete, hasRequestedGalleryOcr]);
    const isYouTubeLink = React.useMemo(() => {
        const rawUrl = String(metaData?.url || metaData?.media_url || '').trim();
        if (!rawUrl) {
            return false;
        }

        try {
            const parsedUrl = new URL(/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`);
            const host = parsedUrl.hostname.toLowerCase();
            return host === 'youtube.com' ||
                host === 'www.youtube.com' ||
                host === 'm.youtube.com' ||
                host === 'music.youtube.com' ||
                host === 'youtu.be';
        } catch {
            return false;
        }
    }, [lnT, metaData?.media_url, metaData?.url]);
    const isSocialVideoLink = React.useMemo(() => {
        const rawUrl = String(metaData?.url || metaData?.media_url || '').trim();
        if (!rawUrl) {
            return false;
        }

        const hasTranscribableVideoMeta = Boolean(
            (metaData?.media_url || metaData?.media_cdn_url) && metaData?.media_has_audio !== false
        ) || (Array.isArray(metaData?.gallery) && metaData.gallery.some(slide => (
            String(slide?.type || '').toLowerCase() === 'video' &&
            (slide?.media_url || slide?.media_cdn_url || slide?.media_hls_url || slide?.media?.url || slide?.media?.cdn_url) &&
            slide?.media_has_audio !== false
        )));

        try {
            const parsedUrl = new URL(/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`);
            const host = parsedUrl.hostname.toLowerCase();
            if (
                host === 'instagram.com' ||
                host === 'www.instagram.com' ||
                host === 'instagr.am' ||
                host === 'www.instagr.am'
            ) {
                if (String(metaData?.media_type || '').toLowerCase() === 'carousel' && !hasTranscribableVideoMeta) {
                    return false;
                }
                return true;
            }
            if (
                host === 'tiktok.com' ||
                host === 'www.tiktok.com' ||
                host === 'm.tiktok.com' ||
                host === 'vt.tiktok.com'
            ) {
                const path = parsedUrl.pathname.toLowerCase();
                return !path.includes('/photo/') && !path.includes('/@photo/');
            }
            return false;
        } catch {
            return false;
        }
    }, [lnT, metaData?.gallery, metaData?.media_cdn_url, metaData?.media_has_audio, metaData?.media_type, metaData?.media_url, metaData?.url]);
    const isXVideoLink = React.useMemo(() => {
        const rawUrl = String(metaData?.url || metaData?.media_url || metaData?.media_cdn_url || '').trim();
        if (!rawUrl) {
            return false;
        }

        try {
            const parsedUrl = new URL(/^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`);
            const host = parsedUrl.hostname.toLowerCase();
            if (host === 'video.twimg.com') {
                return true;
            }
            if (
                host !== 'x.com' &&
                host !== 'www.x.com' &&
                host !== 'mobile.x.com' &&
                host !== 'twitter.com' &&
                host !== 'www.twitter.com' &&
                host !== 'mobile.twitter.com'
            ) {
                return false;
            }

            return Boolean(
                (metaData?.media_url || metaData?.media_cdn_url) ||
                String(metaData?.media_type || '').toLowerCase() === 'video'
            );
        } catch {
            return false;
        }
    }, [lnT, metaData?.media_cdn_url, metaData?.media_type, metaData?.media_url, metaData?.url]);
    // We only offer frame analysis for media we can persist and inspect. This
    // includes direct video plus supported social previews, but deliberately
    // excludes YouTube because its video is not stored by this flow.
    const hasVideoAnalysisCandidate = React.useMemo(
        () => (
            isUrlMetaVideo(metaData) ||
            Boolean(getUrlMetaVideoSource(metaData)) ||
            isSocialVideoLink ||
            isXVideoLink
        ),
        [lnT, isSocialVideoLink, isXVideoLink, metaData]
    );
    const hasTranscript = React.useMemo(
        () => hasMessageTranscriptForUrl({ message, index }),
        [lnT, message?.transcript, index]
    );
    const titleUsesMarkdown = hasUrlPreviewMarkdownFormatting(normalizedTitle) || /https?:\/\//i.test(normalizedTitle);
    const descriptionUsesMarkdown = hasUrlPreviewMarkdownFormatting(normalizedDescription) || /https?:\/\//i.test(normalizedDescription);
    const shouldCollapseTitle = normalizedTitle.length > URL_PREVIEW_TITLE_COLLAPSE_LIMIT;
    const shouldCollapseDescription =
        normalizedDescription.length > URL_PREVIEW_DESCRIPTION_COLLAPSE_LIMIT;
    const displayedTitle = isTitleExpanded
        ? normalizedTitle
        : truncateUrlPreviewText(normalizedTitle, URL_PREVIEW_TITLE_COLLAPSE_LIMIT);
    const displayedDescription = isDescriptionExpanded
        ? normalizedDescription
        : truncateUrlPreviewText(normalizedDescription, URL_PREVIEW_DESCRIPTION_COLLAPSE_LIMIT);
    const markdownBlockResetStyle = React.useMemo(
        () => ({
            margin: 0,
            fontSize: 'inherit',
            lineHeight: 'inherit',
            color: 'inherit',
            whiteSpace: 'pre-wrap',
            wordBreak: 'normal',
            overflowWrap: 'anywhere'
        }),
        [lnT]
    );
    const titleMarkdownOptions = React.useMemo(
        () => ({
            disableParsingRawHTML: true,
            overrides: {
                p: { props: { style: { ...markdownBlockResetStyle, marginBottom: 8 } } },
                strong: { props: { style: { fontWeight: 700, color: 'rgba(255,255,255,0.85)' } } },
                em: { props: { style: { fontStyle: 'italic' } } },
                a: {
                    props: {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        onClick: event => event.stopPropagation(),
                        style: {
                            color: previewLinkTextColor,
                            textDecoration: 'underline',
                            fontWeight: 600
                        }
                    }
                },
                h1: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700, marginBottom: 8 } } },
                h2: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700, marginBottom: 8 } } },
                h3: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700, marginBottom: 8 } } },
                h4: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700, marginBottom: 8 } } },
                h5: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700, marginBottom: 8 } } },
                h6: { props: { style: { ...markdownBlockResetStyle, fontWeight: 700, marginBottom: 8 } } },
                ul: { props: { style: { ...markdownBlockResetStyle, paddingLeft: '16px', listStyleType: 'disc' } } },
                ol: { props: { style: { ...markdownBlockResetStyle, paddingLeft: '16px', listStyleType: 'decimal' } } },
                li: { props: { style: markdownBlockResetStyle } },
                blockquote: { props: { style: { ...markdownBlockResetStyle, borderLeft: `3px solid ${PRIMARY_COLOR}`, paddingLeft: '8px' } } },
                code: { props: { style: { backgroundColor: 'rgba(255, 255, 255, 0.14)', padding: '1px 4px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '0.95em', lineHeight: 'inherit', color: 'rgba(255, 255, 255, 0.9)' } } }
            }
        }),
        [lnT, markdownBlockResetStyle, previewLinkTextColor]
    );
    const descriptionMarkdownOptions = React.useMemo(
        () => ({
            disableParsingRawHTML: true,
            overrides: {
                p: { props: { style: { ...markdownBlockResetStyle, marginBottom: '8px', fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.8)' } } },
                strong: { props: { style: { fontWeight: 'bold', fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.85)' } } },
                em: { props: { style: { fontStyle: 'italic', fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.8)' } } },
                a: { props: { target: '_blank', rel: 'noopener noreferrer', onClick: event => event.stopPropagation(), style: { color: previewLinkTextColor, textDecoration: 'underline', fontSize: '14px', lineHeight: '20px', fontWeight: 500 } } },
                h1: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '4px 0 2px 0', fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.85)' } } },
                h2: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '3px 0 2px 0', fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.85)' } } },
                h3: { props: { style: { ...markdownBlockResetStyle, fontWeight: 'bold', margin: '2px 0 1px 0', fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.85)' } } },
                ul: { props: { style: { ...markdownBlockResetStyle, margin: '4px 0', paddingLeft: '16px', fontSize: '14px', lineHeight: '20px', listStyleType: 'disc', color: 'rgba(255, 255, 255, 0.8)' } } },
                ol: { props: { style: { ...markdownBlockResetStyle, margin: '4px 0', paddingLeft: '16px', fontSize: '14px', lineHeight: '20px', listStyleType: 'decimal', color: 'rgba(255, 255, 255, 0.8)' } } },
                li: { props: { style: { ...markdownBlockResetStyle, margin: '2px 0', fontSize: '14px', lineHeight: '20px', display: 'list-item', color: 'rgba(255, 255, 255, 0.8)' } } },
                blockquote: { props: { style: { ...markdownBlockResetStyle, borderLeft: `3px solid ${PRIMARY_COLOR}`, paddingLeft: '8px', margin: '4px 0', fontStyle: 'italic', fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.8)' } } },
                code: { props: { style: { backgroundColor: 'rgba(255, 255, 255, 0.14)', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace', fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.9)' } } }
            }
        }),
        [lnT, markdownBlockResetStyle, previewLinkTextColor]
    );

    React.useEffect(() => {
        if (!showActionMenu) {
            return undefined;
        }

        const handleOutsideClick = event => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setShowActionMenu(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [showActionMenu]);

    const broadcastMessageUpdated = React.useCallback(updatedMessage => {
        if (!updatedMessage?.id || typeof window === 'undefined') {
            return;
        }

        window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: updatedMessage } }));
    }, [lnT]);

    const handleRefreshBookmarkPreview = React.useCallback(async () => {
        if (readOnly || !canGenerate) return;
        if (!message?.id || isRefreshingPreview) {
            return;
        }

        setIsRefreshingPreview(true);
        setShowActionMenu(false);

        try {
            const result = await refreshBookmarkPreviewMutation({
                variables: {
                    data: {
                        messageId: message.id
                    }
                }
            });

            const updatedMessage = result?.data?.refreshBookmarkPreview;
            if (updatedMessage) {
                broadcastMessageUpdated(updatedMessage);
            }
        } catch (error) {
            showDefaultNotification(message?.id, error?.message || 'Failed to refresh bookmark preview.');
        } finally {
            setIsRefreshingPreview(false);
        }
    }, [readOnly, canGenerate, lnT, broadcastMessageUpdated, isRefreshingPreview, message?.id, refreshBookmarkPreviewMutation, showDefaultNotification]);

    const handleAnalyzeVideo = React.useCallback(() => {
        if (readOnly || !canGenerate) return;
        setShowActionMenu(false);
        mediaToolsRef.current?.analyzeVideo?.();
    }, [readOnly, canGenerate, lnT]);

    const handleVideoAnalysisStateChange = React.useCallback(nextState => {
        setVideoAnalysisState(previousState => (
            previousState.canAnalyze === nextState.canAnalyze &&
            previousState.completed === nextState.completed &&
            previousState.canReanalyze === nextState.canReanalyze &&
            previousState.processing === nextState.processing
                ? previousState
                : nextState
        ));
    }, [lnT]);

    const handleToggleEditMode = React.useCallback(() => {
        if (readOnly) return;
        setShowActionMenu(false);
        setIsEditable(previous => {
            const nextValue = !previous;
            if (nextValue) {
                customAnalytics.ui_action({
                    action: 'Open Link Edit'
                });
            }
            return nextValue;
        });
    }, [readOnly, lnT]);

    const notifyBackground = React.useCallback(title => {
        if (!title) {
            return;
        }

        showDefaultNotification(createClientRequestId('url-preview-notification'), title);
    }, [lnT, showDefaultNotification]);

    const handleSavePagePdf = React.useCallback(async () => {
        if (readOnly || !canGenerate) return;
        const pageUrl = String(metaData?.url || metaData?.media_url || '').trim();
        if (!message?.id || !pageUrl || isSavingPagePdf) {
            return;
        }

        setIsSavingPagePdf(true);
        setShowActionMenu(false);
        notifyBackground('Saving page PDF in background...');

        try {
            let targetMessageId = message.id;
            let savedAsNewMessage = false;

            if (message.file) {
                if (!message?.folder?.id) {
                    throw new Error("Can't create a new message: missing folder.");
                }

                const createRes = await createMessageMutation({
                    variables: {
                        data: {
                            folderId: message.folder.id,
                            content: buildLinkMessageContent({ message, metaData, pageUrl })
                        }
                    }
                });

                const newMessageId = createRes?.data?.createMessage?.id;
                if (!newMessageId) {
                    throw new Error('Failed to create message');
                }

                targetMessageId = newMessageId;
                savedAsNewMessage = true;
            }

            const response = await generateUrlPdfMutation({
                variables: {
                    data: {
                        messageId: targetMessageId,
                        url: pageUrl,
                        displayName: resolvePdfDisplayName({ metaData, message, pageUrl }),
                        clientRequestId: createClientRequestId('url-pdf')
                    }
                }
            });

            const pdfMedia = response?.data?.generateUrlPdf;
            if (!pdfMedia?.url) {
                throw new Error('Failed to create PDF');
            }

            if (!savedAsNewMessage) {
                broadcastMessageUpdated({
                    ...message,
                    file: pdfMedia,
                    updatedAt: new Date().toISOString()
                });
                notifyBackground('PDF saved to message.');
            } else {
                broadcastMessageUpdated({
                    id: targetMessageId,
                    folder: message?.folder || null,
                    file: pdfMedia,
                    updatedAt: new Date().toISOString()
                });
                notifyBackground('PDF saved as a new message.');
            }

            customAnalytics.message_action({
                action: savedAsNewMessage ? 'Save Page PDF as New Message' : 'Save Page PDF',
                type: 'Link'
            }, message);
        } catch (error) {
            showDefaultNotification(message?.id, error?.message || 'Failed to create PDF.');
        } finally {
            setIsSavingPagePdf(false);
        }
    }, [readOnly, canGenerate, lnT,
        broadcastMessageUpdated,
        createMessageMutation,
        generateUrlPdfMutation,
        isSavingPagePdf,
        message,
        metaData,
        notifyBackground,
        showDefaultNotification
    ]);

    const handleSaveRepository = React.useCallback(async () => {
        if (readOnly || !canGenerate) return;
        const pageUrl = String(metaData?.url || metaData?.media_url || '').trim();
        if (!message?.id || !isGithubRepositoryUrl(pageUrl) || repositorySaveInFlight.current || String(message.id).startsWith('temp-') || String(message.id).startsWith('x-')) {
            return;
        }

        repositorySaveInFlight.current = true;
        setIsSavingRepository(true);
        setShowActionMenu(false);
        notifyBackground(lnT('messages:savingRepositoryZip'));

        try {
            let targetMessageId = message.id;
            let savedAsNewMessage = false;

            if (message.file) {
                if (!message?.folder?.id) {
                    throw new Error("Can't create a new message: missing folder.");
                }

                const createRes = await createMessageMutation({
                    variables: {
                        data: {
                            folderId: message.folder.id,
                            content: buildLinkMessageContent({ message, metaData, pageUrl })
                        }
                    }
                });

                const newMessageId = createRes?.data?.createMessage?.id;
                if (!newMessageId) {
                    throw new Error('Failed to create message');
                }

                targetMessageId = newMessageId;
                savedAsNewMessage = true;
            }

            const response = await saveGithubRepositoryMutation({
                variables: {
                    data: {
                        messageId: targetMessageId,
                        url: pageUrl
                    }
                }
            });

            const repositoryMedia = response?.data?.saveGithubRepository;
            if (!repositoryMedia?.url) {
                throw new Error(lnT('messages:repositoryZipFailed'));
            }

            if (!savedAsNewMessage) {
                broadcastMessageUpdated({
                    ...message,
                    file: repositoryMedia,
                    updatedAt: new Date().toISOString()
                });
                notifyBackground(lnT('messages:repositoryZipSaved'));
            } else {
                broadcastMessageUpdated({
                    id: targetMessageId,
                    folder: message?.folder || null,
                    file: repositoryMedia,
                    updatedAt: new Date().toISOString()
                });
                notifyBackground(lnT('messages:repositoryZipSavedAsNewMessage'));
            }

            customAnalytics.message_action({
                action: savedAsNewMessage ? 'Save Repository ZIP as New Message' : 'Save Repository ZIP',
                type: 'Link'
            }, message);
        } catch (error) {
            showDefaultNotification(message?.id, error?.message || lnT('messages:repositoryZipFailed'));
        } finally {
            repositorySaveInFlight.current = false;
            setIsSavingRepository(false);
        }
    }, [readOnly, canGenerate, lnT,
        broadcastMessageUpdated,
        createMessageMutation,
        saveGithubRepositoryMutation,
        isSavingRepository,
        message,
        metaData,
        notifyBackground,
        showDefaultNotification
    ]);

    const handleSavePageScreenshot = React.useCallback(async () => {
        if (readOnly || !canGenerate) return;
        const pageUrl = String(metaData?.url || metaData?.media_url || '').trim();
        if (!message?.id || !pageUrl || isSavingPageScreenshot) {
            return;
        }

        setIsSavingPageScreenshot(true);
        setShowActionMenu(false);
        notifyBackground('Saving page screenshot in background...');

        try {
            let targetMessageId = message.id;
            let savedAsNewMessage = false;

            if (message.file || message.image) {
                if (!message?.folder?.id) {
                    throw new Error("Can't create a new message: missing folder.");
                }

                const createRes = await createMessageMutation({
                    variables: {
                        data: {
                            folderId: message.folder.id,
                            content: buildLinkMessageContent({ message, metaData, pageUrl })
                        }
                    }
                });

                const newMessageId = createRes?.data?.createMessage?.id;
                if (!newMessageId) {
                    throw new Error('Failed to create message');
                }

                targetMessageId = newMessageId;
                savedAsNewMessage = true;
            }

            const response = await generateUrlScreenshotMutation({
                variables: {
                    data: {
                        messageId: targetMessageId,
                        url: pageUrl,
                        displayName: resolveScreenshotDisplayName({ metaData, message, pageUrl }),
                        clientRequestId: createClientRequestId('url-screenshot')
                    }
                }
            });

            const screenshotMedia = response?.data?.generateUrlScreenshot;
            if (!screenshotMedia?.url) {
                throw new Error('Failed to create screenshot');
            }

            if (!savedAsNewMessage) {
                broadcastMessageUpdated({
                    ...message,
                    image: screenshotMedia,
                    updatedAt: new Date().toISOString()
                });
                notifyBackground('Screenshot saved to message.');
            } else {
                broadcastMessageUpdated({
                    id: targetMessageId,
                    folder: message?.folder || null,
                    image: screenshotMedia,
                    updatedAt: new Date().toISOString()
                });
                notifyBackground('Screenshot saved as a new message.');
            }

            customAnalytics.message_action({
                action: savedAsNewMessage ? 'Save Page Screenshot as New Message' : 'Save Page Screenshot',
                type: 'Link'
            }, message);
        } catch (error) {
            showDefaultNotification(message?.id, error?.message || 'Failed to create screenshot.');
        } finally {
            setIsSavingPageScreenshot(false);
        }
    }, [readOnly, canGenerate, lnT,
        broadcastMessageUpdated,
        createMessageMutation,
        generateUrlScreenshotMutation,
        isSavingPageScreenshot,
        message,
        metaData,
        notifyBackground,
        showDefaultNotification
    ]);

    const handleSaveRedditJson = React.useCallback(async () => {
        if (readOnly || !canGenerate) return;
        if (!message?.id || !canSaveRedditJson || isSavingRedditJson) {
            return;
        }

        const messageId = String(message.id);
        if (messageId.startsWith('x-') || messageId.startsWith('temp-')) {
            showDefaultNotification(message?.id || createClientRequestId('reddit-json-error'), 'Save this message first, then get comments.');
            return;
        }

        setIsSavingRedditJson(true);
        setShowActionMenu(false);
        notifyBackground('Saving Reddit comments JSON in background...');

        try {
            let targetMessageId = message.id;
            let savedAsNewMessage = false;

            if (message.file) {
                if (!message?.folder?.id) {
                    throw new Error("Can't create a new message: missing folder.");
                }

                const createRes = await createMessageMutation({
                    variables: {
                        data: {
                            folderId: message.folder.id,
                            content: buildLinkMessageContent({ message, metaData, pageUrl: redditJsonUrl })
                        }
                    }
                });

                const newMessageId = createRes?.data?.createMessage?.id;
                if (!newMessageId) {
                    throw new Error('Failed to create message');
                }

                targetMessageId = newMessageId;
                savedAsNewMessage = true;
            }

            const response = await downloadRedditJsonDumpMutation({
                variables: {
                    data: {
                        messageId: targetMessageId,
                        urlMetaIndex: index
                    }
                }
            });

            const redditJsonMedia = response?.data?.downloadRedditJsonDump;
            if (!redditJsonMedia?.url) {
                throw new Error('Failed to save Reddit comments JSON');
            }

            if (!savedAsNewMessage) {
                broadcastMessageUpdated({
                    ...message,
                    file: redditJsonMedia,
                    updatedAt: new Date().toISOString()
                });
                notifyBackground('Reddit comments JSON saved to message.');
            } else {
                broadcastMessageUpdated({
                    id: targetMessageId,
                    folder: message?.folder || null,
                    file: redditJsonMedia,
                    updatedAt: new Date().toISOString()
                });
                notifyBackground('Reddit comments JSON saved as a new message.');
            }

            customAnalytics.message_action({
                action: savedAsNewMessage ? 'Save Reddit Comments JSON as New Message' : 'Save Reddit Comments JSON',
                type: 'Link'
            }, message);
        } catch (error) {
            showDefaultNotification(message?.id, error?.message || 'Failed to save Reddit comments JSON.');
        } finally {
            setIsSavingRedditJson(false);
        }
    }, [readOnly, canGenerate, lnT,
        broadcastMessageUpdated,
        canSaveRedditJson,
        createMessageMutation,
        downloadRedditJsonDumpMutation,
        index,
        isSavingRedditJson,
        message,
        metaData,
        notifyBackground,
        redditJsonUrl,
        showDefaultNotification
    ]);

    const handleOpenMediaTools = React.useCallback(() => {
        setShowActionMenu(false);
        mediaToolsRef.current?.openToolsModal?.();
    }, [lnT]);

    const handleSaveArticle = React.useCallback(() => {
        if (readOnly || !canGenerate) return;
        setShowActionMenu(false);
        mediaToolsRef.current?.saveArticle?.();
    }, [readOnly, canGenerate, lnT]);

    const handleExtractAllGalleryOcr = React.useCallback(async () => {
        if (readOnly || !canGenerate) return;
        if (isExtractingGalleryOcr || !hasGalleryActions) {
            return;
        }

        setIsExtractingGalleryOcr(true);
        setShowActionMenu(false);
        try {
            await extractCarouselOcrMutation({
                variables: {
                    data: {
                        messageId: message?._resolvedServerId || message?.id
                    }
                }
            });
            setHasRequestedGalleryOcr(true);
            showDefaultNotification(message?.id, 'Carousel text is being added to this message');
        } catch (_error) {
            showDefaultNotification(message?.id, 'Could not start carousel text extraction');
        } finally {
            setIsExtractingGalleryOcr(false);
        }
    }, [readOnly, canGenerate, lnT, extractCarouselOcrMutation, hasGalleryActions, isExtractingGalleryOcr, message?._resolvedServerId, message?.id, showDefaultNotification]);

    const requestAnalysisOcr = React.useCallback(async () => {
        if (readOnly || !canGenerate) return;
        setShowActionMenu(false);
        showDefaultNotification(message?.id, lnT('messages:reAnalysing'));
        await extractCarouselOcrMutation({ variables: { data: {
            messageId: message?._resolvedServerId || message?.id
        } } });
        setHasRequestedGalleryOcr(true);
    }, [readOnly, canGenerate, extractCarouselOcrMutation, message?._resolvedServerId, message?.id, showDefaultNotification, lnT]);

    const analyseCarousel = React.useCallback(async () => {
        if (readOnly || !canGenerate) return;
        setShowActionMenu(false);
        showDefaultNotification(message?.id, lnT('messages:reAnalysing'));
        const result = await extractCarouselArtifactsMutation({ variables: { data: {
            messageId: message?._resolvedServerId || message?.id
        } } });
        const updatedMessage = result?.data?.extractCarouselArtifacts;
        if (updatedMessage) broadcastMessageUpdated(updatedMessage);
        customAnalytics.message_action({ action: 'Extract carousel artifacts', type: 'Link' }, message);
    }, [readOnly, canGenerate, extractCarouselArtifactsMutation, message, broadcastMessageUpdated, showDefaultNotification, lnT]);

    const handleAnalysisError = React.useCallback(error => {
        showDefaultNotification(message?.id, error?.message || lnT('messages:couldNotStartCarouselTextExtraction'));
    }, [message?.id, showDefaultNotification, lnT]);

    const { start: handleExtractCarouselArtifacts, isRunning: isExtractingCarouselArtifacts } = useCarouselReanalysis({
        messageId: message?._resolvedServerId || message?.id,
        ocrComplete: galleryOcrIsComplete,
        requestOcr: requestAnalysisOcr,
        analyse: analyseCarousel,
        onError: handleAnalysisError
    });

    const handleCopyAllGalleryOcr = React.useCallback(async () => {
        if (!allGalleryOcrText || isCopyingGalleryOcr) {
            return;
        }

        setIsCopyingGalleryOcr(true);
        setShowActionMenu(false);
        try {
            await navigator.clipboard.writeText(allGalleryOcrText);
            showDefaultNotification(message?.id, 'All carousel text copied');
            customAnalytics.message_action({
                action: 'Copy All Carousel Text',
                type: 'Link'
            }, message);
        } catch (_error) {
            showDefaultNotification(message?.id, 'Could not copy carousel text');
        } finally {
            setIsCopyingGalleryOcr(false);
        }
    }, [lnT, allGalleryOcrText, isCopyingGalleryOcr, message, showDefaultNotification]);

    const handleDownloadAllGalleryMedia = React.useCallback(async () => {
        if (isDownloadingGallery || !hasGalleryActions) {
            return;
        }

        setIsDownloadingGallery(true);
        setShowActionMenu(false);
        try {
            const files = [];
            for (let slideIndex = 0; slideIndex < gallerySlides.length; slideIndex += 1) {
                const slide = gallerySlides[slideIndex];
                const isVideo = !isImageGallerySlide(slide);
                const blob = await fetchGalleryMediaBlob([
                    ...(isVideo ? [
                        slide?.media?.url,
                        slide?.media_cdn_url,
                        slide?.media_url,
                        slide?.media?.cdn_url
                    ] : [
                        slide?.media?.cdn_url,
                        slide?.media?.url,
                        slide?.media_cdn_url,
                        slide?.media_url
                    ]),
                    slide?.image_cdn_url,
                    slide?.image
                ], isVideo ? 'video' : 'image');
                if (!blob) {
                    continue;
                }

                const filename = `carousel-${isVideo ? 'video' : 'image'}-${String(slideIndex + 1).padStart(2, '0')}.${getMediaExtension(blob)}`;
                files.push({ name: filename, blob });
            }

            if (files.length === 0) {
                throw new Error('No gallery media could be downloaded');
            }

            const zipBlob = await createZipBlob(files);
            const zipFileName = buildZipDownloadFileName(metaData?.title || metaData?.image_name);
            if (isTauri) {
                const saved = await saveBlobWithTauri(zipBlob, zipFileName);
                if (!saved) {
                    return;
                }
            } else {
                downloadBlobInBrowser(zipBlob, zipFileName);
            }

            showDefaultNotification(
                message?.id,
                files.length === gallerySlides.length
                    ? 'Carousel ZIP downloaded'
                    : 'Carousel ZIP downloaded; some media was unavailable'
            );
        } catch (_error) {
            showDefaultNotification(message?.id, 'Some carousel media could not be downloaded');
        } finally {
            setIsDownloadingGallery(false);
        }
    }, [lnT, gallerySlides, hasGalleryActions, isDownloadingGallery, isTauri, message?.id, showDefaultNotification]);

    const handlePreviewToggleMouseDown = React.useCallback(event => {
        event.preventDefault();
        event.stopPropagation();
    }, [lnT]);
    const handlePreviewToggleClick = React.useCallback((event, toggleExpanded) => {
        event.preventDefault();
        event.stopPropagation();
        toggleExpanded(previousValue => !previousValue);
    }, [lnT]);
    const openTranscriptWorkspace = React.useCallback(() => {
        if (!hasTranscript || typeof onOpenVideoWorkspace !== 'function') {
            return;
        }

        onOpenVideoWorkspace(message, {
            src: getUrlMetaVideoSource(metaData) || '',
            hlsUrl: getUrlMetaDirectS3VideoSource(metaData) ? '' : metaData?.media_hls_url || '',
            originalUrl: metaData?.url || metaData?.media_url || '',
            embedUrl: metaData?.embed || '',
            url: metaData?.url || ''
        });
    }, [lnT, hasTranscript, message, metaData, onOpenVideoWorkspace]);

    React.useEffect(() => {
        setIsTitleExpanded(false);
    }, [normalizedTitle]);

    React.useEffect(() => {
        setIsDescriptionExpanded(false);
    }, [normalizedDescription]);

    // const metaActive = holdingKeys && (keys.Meta || keys.Control);

    // const checkIfEmpty = async () => {
    //     let urlMeta = message.urlMeta[index];
    //     if (!urlMeta.description && !urlMeta.image && !urlMeta.publisher && !urlMeta.title) {
    //         await message.urlMeta.splice(index, 1);
    //     }
    // };

    const deleteAll = async () => {
        if (readOnly) return;
        await message.urlMeta.splice(index, 1, {});
        updateURL(message, message.urlMeta);
    };

    const deleteMeta = async () => {
        if (readOnly) return;
        await message.urlMeta.splice(index, 1, { url: message.urlMeta[index].url, logo: message.urlMeta[index].logo });
        updateURL(message, message.urlMeta);
    };

    const deleteUrlImage = () => {
        message.urlMeta[index].image = null;
        // checkIfEmpty();
        updateURL(message, message.urlMeta);
    };

    const deletePublisher = () => {
        message.urlMeta[index].publisher = null;
        // checkIfEmpty();
        updateURL(message, message.urlMeta);
    };

    const deleteTitle = () => {
        if (readOnly) return;
        message.urlMeta[index].title = null;
        // checkIfEmpty();
        updateURL(message, message.urlMeta);
    };

    const deleteDescription = () => {
        if (readOnly) return;
        message.urlMeta[index].description = null;
        // checkIfEmpty();
        updateURL(message, message.urlMeta);
    };

    const removeButton = (callback, ariaLabel = 'Remove section') => {
        if (isEditable) {
            return (
                <button
                    type="button"
                    aria-label={ariaLabel}
                    onClick={callback}
                    style={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        zIndex: 2,
                        width: 22,
                        height: 22,
                        minWidth: 22,
                        borderRadius: 11,
                        border: '1px solid rgba(239, 68, 68, 0.38)',
                        backgroundColor: 'rgba(239, 68, 68, 0.18)',
                        padding: 0
                    }}
                >
                    <IoIosClose color="rgba(255, 255, 255, 0.92)" size={14}></IoIosClose>
                </button>
            );
        }
        return null;
    };

    // const removeSection = callback => {
    //     if (metaActive) {
    //         return (
    //             <div onClick={callback} style={{ color: 'white', paddingLeft: 8, marginBottom: 16, cursor: 'pointer' }}>
    //                 Remove preview
    //             </div>
    //         );
    //     }
    // };

    const renderOpenOrRemove = () => {
        if (!isEditable) {
            return (
                <>
                {hasArticleBody ? <div style={{ marginBottom: 8 }}>
                        <FooterReadButton
                            type="button"
                            onPointerDown={event => {
                                event.stopPropagation();
                            }}
                            onMouseDown={event => {
                                event.stopPropagation();
                            }}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                openArticleWorkspace();
                            }}
                        >
                            <FiBookOpen size={13} />
                            <span>{lnT('messages:read')}</span>
                        </FooterReadButton>
</div> : null}
                <FooterPrimaryRow>
                    {renderSourceLabel()}

                    {renderOpenButton()}
                </FooterPrimaryRow>
                </>
            );
        }

        return (
            <FooterEditActions>
                <FooterPrimaryRow>
                    {renderSourceLabel()}
                </FooterPrimaryRow>
                <FooterSecondaryRow>
                    {metaData.description || metaData.title || metaData.image ? (
                        <FooterActionButton
                            type="button"
                            $variant="primary"
                            onClick={() => {
                                deleteMeta();
                                setIsEditable(!isEditable);
                                customAnalytics.message_edit({
                                    'action': 'Keep',
                                    "type": "Link"
                                }, message);
                            }}
                        >{lnT('messages:keepLink')}</FooterActionButton>
                    ) : null}
                    <FooterActionButton
                        type="button"
                        $variant="danger"
                        onClick={() => {
                            deleteAll()
                            customAnalytics.message_edit({
                                "action": 'Delete',
                                "type": "Link"
                            }, message);
                        }}
                    >{lnT('common:delete')}</FooterActionButton>
                </FooterSecondaryRow>
            </FooterEditActions>
        );
    };

    const renderSourceLabel = () => {
        if (!metaData?.url) {
            return null;
        }

        return <UrlPreviewSource metaData={metaData} />;
    };

    const renderOpenButton = () => {
        if (isEditable || !metaData?.url) {
            return null;
        }

        return (
            <FooterOpenLink
                style={{ color: FILE_BLUE }}
                href={metaData.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                    customAnalytics.message_action({
                        'action': 'Open',
                        "type": "Link"
                    }, message);
                }}
            >{lnT('common:open')}</FooterOpenLink>
        );
    };

    const renderSettingsButton = () => {
        if (readOnly) return null;
        let urlMeta = message.urlMeta[index];
        if (urlMeta) {
            if (isEditable) {
                return (
                    <EditCloseButton
                        type="button"
                        aria-label={lnT('messages:closeBookmarkPreviewEditMode')}
                        onClick={handleToggleEditMode}
                    >
                        <IoIosClose name="close" size={18} color="rgba(255, 255, 255, 0.85)" />
                    </EditCloseButton>
                );
            }

            return (
                <ActionMenuContainer
                    ref={actionMenuRef}
                    style={{
                        display: 'flex',
                    }}
                >
                    <ActionMenuTrigger
                        type="button"
                        aria-label={lnT('messages:openBookmarkPreviewActions')}
                        onClick={() => {
                            setShowActionMenu(previous => !previous);
                        }}
                    >
                        <FiMoreVertical size={16} color="rgba(255,255,255,.8)" />
                    </ActionMenuTrigger>
                    {showActionMenu ? (
                        <ActionMenu>
                            {!readOnly ? <>
                            <ActionMenuItem
                                type="button"
                                onClick={handleToggleEditMode}
                            >
                                <ActionMenuItemLabel>
                                    <FiEdit3 size={14} />{lnT('messages:editPreview')}</ActionMenuItemLabel>
                            </ActionMenuItem>
                            {canGenerate ? (
                            <ActionMenuItem
                                type="button"
                                onClick={handleRefreshBookmarkPreview}
                                disabled={!canGenerate || isRefreshingPreview}
                            >
                                <ActionMenuItemLabel>
                                    <FiRefreshCw size={14} />
                                    {isRefreshingPreview ? lnT('messages:refreshingPreview') : lnT('messages:refreshPreview')}
                                </ActionMenuItemLabel>
                            </ActionMenuItem>
                            ) : null}
                            {canGenerate && hasVideoAnalysisCandidate ? (
                                <ActionMenuItem
                                    type="button"
                                    onClick={handleAnalyzeVideo}
                                    disabled={videoAnalysisState.processing}
                                >
                                    <ActionMenuItemLabel>
                                        <LuScanText size={14} />
                                        {videoAnalysisState.processing ? lnT('messages:reanalyzingVideo_388f4974') : lnT('messages:reanalyzeVideo')}
                                    </ActionMenuItemLabel>
                                </ActionMenuItem>
                            ) : null}
                            </> : null}
                            {hasGalleryActions ? (
                                <>
                                    {galleryOcrIsComplete && allGalleryOcrText ? (
                                        <ActionMenuItem
                                            type="button"
                                            onClick={handleCopyAllGalleryOcr}
                                            disabled={isCopyingGalleryOcr}
                                        >
                                            <ActionMenuItemLabel>
                                                <FiCopy size={14} />
                                                {isCopyingGalleryOcr ? lnT('messages:copyingText') : lnT('messages:copyAllImageText')}
                                            </ActionMenuItemLabel>
                                        </ActionMenuItem>
                                    ) : !readOnly && canGenerate && !galleryOcrIsComplete ? (
                                        <ActionMenuItem
                                            type="button"
                                            onClick={handleExtractAllGalleryOcr}
                                            disabled={galleryOcrIsPending}
                                        >
                                            <ActionMenuItemLabel>
                                                <LuScanText size={14} />
                                                {galleryOcrIsPending ? lnT('messages:extractingImageText') : lnT('messages:extractAllImageText')}
                                            </ActionMenuItemLabel>
                                        </ActionMenuItem>
                                    ) : null}
                                    {!readOnly && canGenerate ? <>
                                    <ActionMenuItem
                                        type="button"
                                        onClick={handleExtractCarouselArtifacts}
                                        disabled={isExtractingCarouselArtifacts}
                                    >
                                        <ActionMenuItemLabel>
                                            <FiBookOpen size={14} />
                                            {isExtractingCarouselArtifacts ? lnT('messages:reAnalysing') : lnT('messages:reAnalyse')}
                                        </ActionMenuItemLabel>
                                    </ActionMenuItem>
                                    </> : null}
                                    <ActionMenuItem
                                        type="button"
                                        onClick={handleDownloadAllGalleryMedia}
                                        disabled={isDownloadingGallery}
                                    >
                                        <ActionMenuItemLabel>
                                            <FiDownload size={14} />
                                            {isDownloadingGallery ? lnT('messages:creatingZip') : lnT('messages:downloadGalleryZip')}
                                        </ActionMenuItemLabel>
                                    </ActionMenuItem>
                                </>
                            ) : null}
                            {!readOnly && canGenerate ? <>
                            {hasUrlMediaToolsEntry ? (
                                <ActionMenuItem type="button" onClick={handleOpenMediaTools}>
                                    <ActionMenuItemLabel>
                                        <FiTool size={14} />{lnT('messages:mediaTools')}</ActionMenuItemLabel>
                                </ActionMenuItem>
                            ) : null}
                            {hasPageUrl ? (
                                <ActionMenuItem type="button" onClick={handleSaveArticle}>
                                    <ActionMenuItemLabel>
                                        <FiBookOpen size={14} />{lnT('messages:saveArticle')}</ActionMenuItemLabel>
                                </ActionMenuItem>
                            ) : null}
                            {isGithubRepositoryUrl(metaData?.url || metaData?.media_url) ? (
                                <ActionMenuItem
                                    type="button"
                                    onClick={handleSaveRepository}
                                    disabled={isSavingRepository || !message?.id || /^(temp-|x-)/.test(String(message.id))}
                                >
                                    <ActionMenuItemLabel>
                                        <FiDownload size={14} />
                                        {isSavingRepository ? lnT('messages:savingRepositoryZip') : lnT('messages:saveRepositoryZip')}
                                    </ActionMenuItemLabel>
                                </ActionMenuItem>
                            ) : null}
                            {canSaveRedditJson ? (
                                <ActionMenuItem
                                    type="button"
                                    onClick={handleSaveRedditJson}
                                    disabled={isSavingRedditJson}
                                >
                                    <ActionMenuItemLabel>
                                        <FiMessageSquare size={14} />
                                        {isSavingRedditJson ? lnT('messages:savingComments') : lnT('messages:getComments')}
                                    </ActionMenuItemLabel>
                                </ActionMenuItem>
                            ) : null}
                            {hasPageUrl ? (
                                <ActionMenuItem
                                    type="button"
                                    onClick={handleSavePagePdf}
                                    disabled={isSavingPagePdf}
                                >
                                    <ActionMenuItemLabel>
                                        <FiFileText size={14} />
                                        {isSavingPagePdf ? lnT('messages:savingPdf') : lnT('messages:savePageAsPdf')}
                                    </ActionMenuItemLabel>
                                </ActionMenuItem>
                            ) : null}
                            {hasPageUrl ? (
                                <ActionMenuItem
                                    type="button"
                                    onClick={handleSavePageScreenshot}
                                    disabled={isSavingPageScreenshot}
                                >
                                    <ActionMenuItemLabel>
                                        <FiImage size={14} />
                                        {isSavingPageScreenshot ? lnT('messages:savingScreenshot') : lnT('messages:savePageScreenshot')}
                                    </ActionMenuItemLabel>
                                </ActionMenuItem>
                            ) : null}
                            {!hideTranscriptAction && (hasTranscript || isYouTubeLink || isSocialVideoLink || isXVideoLink) ? (
                                <UrlTranscriptButton
                                    message={message}
                                    metaData={metaData}
                                    urlMetaIndex={index}
                                    variant="dark"
                                    fullWidth
                                    presentation="menu-item"
                                    hideWhileProcessing
                                    onShowTranscript={() => {
                                        setShowActionMenu(false);
                                        openTranscriptWorkspace();
                                    }}
                                >
                                    <ActionMenuItemLabel>
                                        <FiFileText size={14} />
                                        {hasTranscript ? lnT('messages:showTranscript') : lnT('messages:getTranscript')}
                                    </ActionMenuItemLabel>
                                </UrlTranscriptButton>
                            ) : null}
                            </> : null}
                        </ActionMenu>
                    ) : null}
                </ActionMenuContainer>
            );
        }
    };

    // Setup ResizeObserver - measure container width to determine if we should wrap
    React.useEffect(() => {
        let rafId = null;
        let isObserving = true;

        const checkWidth = observedWidth => {
            if (!isObserving) return;
            if (wrapperRef.current) {
                // Measure the actual container width, not window width
                // This accounts for cases where the component is in a narrow column
                const containerWidth = Number.isFinite(observedWidth)
                    ? observedWidth
                    : wrapperRef.current.getBoundingClientRect().width;
                setMeasuredPreviewWidth(previous => previous === containerWidth ? previous : containerWidth);
                // Below 400px, switch to column layout
                // This allows side-by-side layout to persist longer before wrapping
                setIsSmallScreen(containerWidth < 400);
            }
        };

        let pendingWidth;
        const throttledCheckWidth = entries => {
            const entry = Array.isArray(entries) ? entries[0] : null;
            const borderBox = entry?.borderBoxSize;
            pendingWidth = (Array.isArray(borderBox) ? borderBox[0] : borderBox)?.inlineSize;
            if (!Number.isFinite(pendingWidth) && entry) {
                pendingWidth = entry.contentRect.width;
            }
            if (rafId || !isObserving) return;

            rafId = window.requestAnimationFrame(() => {
                checkWidth(pendingWidth);
                rafId = null;
            });
        };

        // Initial check
        checkWidth();

        // Observe the wrapper for container-specific constraints
        resizeObserverRef.current = new ResizeObserver(throttledCheckWidth);

        if (wrapperRef.current) {
            resizeObserverRef.current.observe(wrapperRef.current);
        }

        // Also listen to window resize as fallback
        window.addEventListener('resize', throttledCheckWidth);

        // Listen for drag start/end
        const handleDragStart = () => {
            isObserving = false;
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };

        const handleDragEnd = () => {
            isObserving = true;
            if (resizeObserverRef.current && wrapperRef.current) {
                resizeObserverRef.current.observe(wrapperRef.current);
            }
            checkWidth();
        };

        document.addEventListener('dragstart', handleDragStart);
        document.addEventListener('dragend', handleDragEnd);

        return () => {
            isObserving = false;
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
            window.removeEventListener('resize', throttledCheckWidth);
            document.removeEventListener('dragstart', handleDragStart);
            document.removeEventListener('dragend', handleDragEnd);
        };
    }, []);

    const isDirectImageUrl = Boolean(getUrlMetaDirectImageUrl(metaData));

    const getUrlMetaThumbnailUrl = (metaData, isAuthor = false) => (
        getUrlMetaImageSources(metaData, isAuthor)[0] || null
    );
    
    const stableGalleryFrameSlide = hasGalleryItems
        ? (gallerySlides[0] || null)
        : null;
    const frameImageSource = getGallerySlidePosterUrl(stableGalleryFrameSlide, metaData?.image || metaData?.url);
    const handlePreviewImageLoad = event => {
        if (hasGalleryItems && currentGalleryIndex !== 0) return;
        const { naturalWidth, naturalHeight } = event.currentTarget;
        if (naturalWidth > 0 && naturalHeight > 0) {
            const ratio = naturalHeight / naturalWidth;
            setLoadedImageFrame(previous => previous?.source === frameImageSource && previous.ratio === ratio
                ? previous : { source: frameImageSource, ratio });
        }
    };
    const effectiveGalleryIndex = React.useMemo(() => {
        if (gallerySlides.length === 0) {
            return 0;
        }

        return Math.max(0, Math.min(currentGalleryIndex, gallerySlides.length - 1));
    }, [lnT, gallerySlides.length, currentGalleryIndex]);
    const activeGallerySlide = gallerySlides[effectiveGalleryIndex] || null;
    const selectGalleryIndex = React.useCallback(nextIndex => {
        if (gallerySlides.length <= 1) {
            return;
        }

        const boundedIndex = Math.max(0, Math.min(nextIndex, gallerySlides.length - 1));
        setCurrentGalleryIndex(previousIndex => (previousIndex === boundedIndex ? previousIndex : boundedIndex));
    }, [lnT, gallerySlides.length]);

    const handleGalleryScrubMove = React.useCallback(event => {
        if (gallerySlides.length <= 1) {
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        if (rect.width <= 0) {
            return;
        }

        const pointerX = Math.max(0, Math.min(event.clientX - rect.left, rect.width - 1));
        selectGalleryIndex(Math.floor((pointerX / rect.width) * gallerySlides.length));
    }, [lnT, gallerySlides.length, selectGalleryIndex]);

    React.useEffect(() => {
        if (gallerySlides.length === 0) {
            if (currentGalleryIndex !== 0) {
                setCurrentGalleryIndex(0);
            }
            return;
        }

        if (currentGalleryIndex >= gallerySlides.length) {
            setCurrentGalleryIndex(0);
        }
    }, [gallerySlides.length, currentGalleryIndex]);

    const previewMetaData = React.useMemo(() => {
        if (!activeGallerySlide) {
            return metaData;
        }

        return {
            ...metaData,
            image: getGallerySlidePosterUrl(activeGallerySlide, metaData.image),
            image_name: activeGallerySlide.image_name,
            public_id: null,
            gallery_images: activeGallerySlide.image ? [activeGallerySlide.image] : [],
            image_cdn_url: getGallerySlidePosterCdnUrl(activeGallerySlide),
            thumbnail: null,
            media_cdn_url: activeGallerySlide.type === 'video' ? activeGallerySlide.media_cdn_url : null,
            media_url: activeGallerySlide.type === 'video' ? activeGallerySlide.media_url : null,
            media_hls_url: activeGallerySlide.type === 'video' ? activeGallerySlide.media_hls_url : null,
            media_type: activeGallerySlide.type,
            media_width: activeGallerySlide.width,
            media_height: activeGallerySlide.height,
            media_duration: activeGallerySlide.media_duration,
            media_has_audio: activeGallerySlide.media_has_audio,
            media: activeGallerySlide.media || null
        };
    }, [lnT, activeGallerySlide, metaData]);
    const carouselItems = React.useMemo(() => {
        if (gallerySlides.length > 0) {
            return gallerySlides.map((slide, galleryIndex) => ({
                ...metaData,
                image: getGallerySlidePosterUrl(slide, metaData.image),
                gallery_images: slide.image ? [slide.image] : [],
                image_cdn_url: getGallerySlidePosterCdnUrl(slide),
                thumbnail: null,
                media_cdn_url: slide.type === 'video' ? slide.media_cdn_url : null,
                media_url: slide.type === 'video' ? slide.media_url : null,
                media_hls_url: slide.type === 'video' ? slide.media_hls_url : null,
                media_type: slide.type,
                media_width: slide.width,
                media_height: slide.height,
                media_duration: slide.media_duration,
                media_has_audio: slide.media_has_audio,
                media: slide.media || null,
                ocrText: slide.media?.ocrText || null,
                ocrExtracted: slide.media?.ocrExtracted === true,
                public_id: null,
                gallery_item_index: galleryIndex
            }));
        }

        return [metaData];
    }, [lnT, gallerySlides, metaData]);
    const openGalleryWorkspace = React.useCallback((initialIndex = effectiveGalleryIndex) => {
        if (typeof onOpenVideoWorkspace !== 'function' || imageGallerySlides.length < 2) {
            return false;
        }

        onOpenVideoWorkspace(message, {
            type: 'image',
            initialIndex,
            originalUrl: metaData?.url || metaData?.media_url || '',
            gallery: gallerySlides.map((slide, index) => ({
                url: getGallerySlidePosterUrl(slide, metaData?.image),
                cdn_url: getGallerySlidePosterCdnUrl(slide),
                displayName: `${metaData?.title || message?.title || 'Image'} ${index + 1}`,
                originalUrl: metaData?.url || metaData?.media_url || '',
                ocrText: slide?.media?.ocrText || slide?.ocrText || ''
            }))
        });
        return true;
    }, [lnT, effectiveGalleryIndex, gallerySlides, imageGallerySlides.length, message, metaData, onOpenVideoWorkspace]);

    // Use thumbnails based on display width (200px max for preview sections)
    const mainImageUrl = getUrlMetaThumbnailUrl(previewMetaData, false);
    const authorImageUrl = getUrlMetaThumbnailUrl(previewMetaData, true);
    const displayImage = mainImageUrl || authorImageUrl;
    const isAuthorImage = !mainImageUrl && !!authorImageUrl;
    const previewS3VideoSource = getUrlMetaDirectS3VideoSource(previewMetaData);
    const previewVideoSource = getUrlMetaVideoSource(previewMetaData);
    const hasVideoPreview = Boolean(inlineYouTubeSource) || isUrlMetaVideo(previewMetaData);
    const hasUrlMediaToolsEntry = hasVideoPreview || isYouTubeLink || isSocialVideoLink || isXVideoLink;
    const showPosterOnlyGalleryPreview = hasVideoPreview && hasGalleryItems && !inlineYouTubeSource;
    const previewImageFrameStyle = React.useMemo(() => {
        if (measuredPreviewWidth <= 0 || isAuthorImage || (hasVideoPreview && !showPosterOnlyGalleryPreview)) {
            return null;
        }

        const stableWidth = Number(
            stableGalleryFrameSlide?.width || stableGalleryFrameSlide?.media_width || 0
        );
        const stableHeight = Number(
            stableGalleryFrameSlide?.height || stableGalleryFrameSlide?.media_height || 0
        );
        const stableAspectRatio = stableWidth > 0 && stableHeight > 0
            ? stableHeight / stableWidth
            : Number(stableGalleryFrameSlide?.aspect_ratio || 0) ||
                (loadedImageFrame?.source === frameImageSource ? loadedImageFrame.ratio : 0);

        if (!(stableAspectRatio > 0)) {
            return null;
        }

        const containerWidth = measuredPreviewWidth;
        const frameWidth = Math.min(320, Math.max(containerWidth - 32, 1), 250 / stableAspectRatio);
        const frameHeight = Math.min(Math.floor(frameWidth * stableAspectRatio), 250);

        return {
            width: frameWidth,
            height: frameHeight
        };
    }, [lnT, stableGalleryFrameSlide, measuredPreviewWidth, loadedImageFrame, frameImageSource, isAuthorImage, hasVideoPreview, showPosterOnlyGalleryPreview]);

    if (metaData) {
        return (
            <>
                <PreviewSection
                    ref={wrapperRef}
                    style={{
                        position: 'relative',
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: `1px solid ${PALE_WHITE_1}`,
                        borderRadius: 12,
                        padding: 8,
                        // maxWidth: 450,
                        ...style
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            padding: 4,
                            width: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            gap: 12
                            // maxWidth: 450
                        }}
                    >
                        {/* {metaData.publisher ? (
                            <UrlSection>
                                {removeButton(deletePublisher)}
                                <div style={{ color: '#333', fontWeight: 600 }}>{metaData.publisher}</div>
                            </UrlSection>
                        ) : null} */}
                        {/* Post image/video */}
                        {(isDirectImageUrl || displayImage || hasVideoPreview) ? (
                            <ImageUrlSection isSmallScreen={isSmallScreen} $isEditable={isEditable}>
                                {removeButton(deleteUrlImage, 'Remove image')}
                                <div
                                    style={{
                                        flex: '0 1 auto',
                                        position: 'relative',
                                        width: previewImageFrameStyle?.width || '100%',
                                        maxWidth: 'min(320px, 100%)',
                                        minWidth: 0,
                                        height: previewImageFrameStyle?.height || 'auto',
                                        marginRight: 0,
                                        marginBottom: 0
                                    }}
                                    onClick={e => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (inlineYouTubeSource) return;
                                        if (openGalleryWorkspace()) return;
                                        if (typeof onOpenVideoWorkspace === 'function' && (!hasVideoPreview || showPosterOnlyGalleryPreview)) {
                                            const imageUrl = isDirectImageUrl ? metaData.url : previewMetaData.image || displayImage;
                                            const imageCdnUrl = isDirectImageUrl ? metaData.url : previewMetaData.image_cdn_url || imageUrl;
                                            onOpenVideoWorkspace(message, {
                                                type: 'image',
                                                src: imageCdnUrl,
                                                image: { url: imageUrl, cdn_url: imageCdnUrl },
                                                displayName: metaData.title || message?.title || 'Image',
                                                originalUrl: metaData.url || imageUrl,
                                                ocrText: previewMetaData.media?.ocrText || previewMetaData.ocrText || ''
                                            });
                                        } else {
                                            setLightboxIsOpen(true);
                                        }
                                    }}
                                    onMouseMove={hasGalleryItems ? handleGalleryScrubMove : undefined}
                                >
                                    {hasGalleryItems ? (
                                        <div
                                            aria-hidden="true"
                                            style={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                zIndex: 3,
                                                padding: '3px 7px',
                                                borderRadius: 999,
                                                background: 'rgba(0, 0, 0, 0.5)',
                                                color: '#ffffff',
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                pointerEvents: 'none'
                                            }}
                                        >
                                            {lnT('messages:text_7b652f58', { value1: String(effectiveGalleryIndex + 1), value2: String(gallerySlides.length) })}
                                        </div>
                                    ) : null}
                                    {isDirectImageUrl ? (
                                        <img
                                            onLoad={handlePreviewImageLoad}
                                            src={metaData.url}
                                            style={{
                                                borderRadius: 8,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                maxHeight: '300px'
                                            }}
                                        />
                                    ) : inlineYouTubeSource ? (
                                        <div style={{ width: '100%', aspectRatio: '16 / 9' }}>
                                            <ReactPlayer
                                                key={inlineYouTubeSource}
                                                url={inlineYouTubeSource}
                                                width="100%"
                                                height="100%"
                                                controls
                                                playsinline
                                                config={{ youtube: { playerVars: { origin: window.location.origin, playsinline: 1 } } }}
                                            />
                                        </div>
                                    ) : hasVideoPreview && !showPosterOnlyGalleryPreview ? (
                                        <RedditVideoPlayer
                                            videoUrl={previewS3VideoSource || getUrlMetaOriginalVideoUrl(previewMetaData) || previewVideoSource}
                                            audioUrl={previewMetaData.media_audio_url}
                                            hlsUrl={previewS3VideoSource ? null : decodeProviderMediaUrl(previewMetaData.media_hls_url)}
                                            poster={displayImage}
                                            width={previewMetaData.media_width}
                                            height={previewMetaData.media_height}
                                            hasAudio={previewMetaData.media_has_audio}
                                        />
                                    ) : isAuthorImage ? (
                                        // Author image fallback - render as regular img with max 100x100px
                                        <RecoverableImage sources={urlMetaImageRecovery(previewMetaData, true).sources} refresh={urlMetaImageRecovery(previewMetaData, true).refresh} allowRefresh={!readOnly}
                                            src={displayImage}
                                            alt={metaData.author || lnT('messages:author')}
                                            style={{
                                                borderRadius: 8,
                                                objectFit: 'cover',
                                                width: 100,
                                                height: 100,
                                                maxWidth: 100,
                                                maxHeight: 100
                                            }}
                                        />
                                    ) : (displayImage && (displayImage.includes('cloudinary.com') || (!displayImage.includes('http') && !displayImage.includes('amazonaws.com') && !displayImage.includes('b-cdn.net')))) ? (
                                        // Cloudinary image - use Cloudinary component
                                        <Image
                                            onLoad={handlePreviewImageLoad}
                                            cloudName={environment.REACT_APP_CLOUD_NAME}
                                            publicId={displayImage}
                                            width={'1.0'}
                                            crop={'pad'}
                                            style={{
                                                borderRadius: 8,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                maxHeight: '250px'
                                            }}
                                        >
                                            <Transformation fetchFormat="auto" flags="lossy" quality="50" />
                                        </Image>
                                    ) : displayImage ? (
                                        // S3 image - use CDN URL with fallback to S3 URL
                                        <RecoverableImage
                                            onLoad={handlePreviewImageLoad}
                                            sources={urlMetaImageRecovery(previewMetaData).sources} refresh={urlMetaImageRecovery(previewMetaData).refresh} allowRefresh={!readOnly}
                                            src={displayImage}
                                            alt={metaData.title || lnT('folders:preview')}
                                            style={{
                                                borderRadius: 8,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                maxHeight: '250px'
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            minWidth: 150,
                                            minHeight: 100,
                                            backgroundColor: PRIMARY_COLOR_1,
                                            borderRadius: 8,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px'
                                        }}>
                                            <ImageNAIcon />
                                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)' }}>
                                                {previewMetaData.media_url ? lnT('messages:videoNotSupported') : lnT('messages:noImageAvailable')}
                                            </div>
                                        </div>
                                    )}
                                    {showPosterOnlyGalleryPreview ? (
                                        <div
                                            aria-hidden="true"
                                            style={{
                                                position: 'absolute',
                                                left: 10,
                                                bottom: 10,
                                                zIndex: 2,
                                                padding: '5px 9px',
                                                borderRadius: 999,
                                                background: 'rgba(0, 0, 0, 0.62)',
                                                color: '#fff',
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                lineHeight: 1.2,
                                                pointerEvents: 'none'
                                            }}
                                        >{lnT('messages:openVideo')}</div>
                                    ) : null}
                                </div>
                            </ImageUrlSection>
                        ) : null}
                        {/* Content section - title first, then description */}
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                            <UrlDescriptionHeader>
                                <UrlDescriptionContent>
                                    {hasNormalizedTitle ? (
                                        <UrlSection $isEditable={isEditable}>
                                            {removeButton(deleteTitle, 'Remove title')}
                                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                                                <div
                                                    style={{
                                                        fontWeight: 600,
                                                        color: 'rgba(255,255,255,0.85)',
                                                        whiteSpace: titleUsesMarkdown ? 'normal' : 'pre-wrap',
                                                        wordBreak: 'normal',
                                                        overflowWrap: 'anywhere'
                                                    }}
                                                >
                                                    {titleUsesMarkdown ? (
                                                        <Markdown options={titleMarkdownOptions}>
                                                            {displayedTitle}
                                                        </Markdown>
                                                    ) : displayedTitle}
                                                </div>
                                                {shouldCollapseTitle && (
                                                    <div
                                                        data-message-interactive="true"
                                                        style={{
                                                            marginTop: '6px',
                                                            cursor: 'pointer',
                                                            color: previewLinkTextColor,
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            alignSelf: 'flex-start'
                                                        }}
                                                        onMouseDown={handlePreviewToggleMouseDown}
                                                        onClick={event => handlePreviewToggleClick(event, setIsTitleExpanded)}
                                                    >
                                                        {isTitleExpanded ? lnT('messages:showLess') : lnT('messages:showMore')}
                                                    </div>
                                                )}
                                            </div>
                                        </UrlSection>
                                    ) : null}
                                    {hasNormalizedDescription ? (
                                        <UrlSection $isEditable={isEditable}>
                                            {removeButton(deleteDescription, 'Remove description')}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <TruncateWrapper>
                                                    {descriptionUsesMarkdown ? (
                                                        <Markdown options={descriptionMarkdownOptions}>
                                                            {displayedDescription}
                                                        </Markdown>
                                                    ) : (
                                                        <div
                                                            style={{
                                                                margin: 0,
                                                                fontSize: '14px',
                                                                lineHeight: '20px',
                                                                color: 'rgba(255, 255, 255, 0.8)',
                                                                wordBreak: 'normal',
                                                                overflowWrap: 'anywhere',
                                                                whiteSpace: 'pre-wrap'
                                                            }}
                                                        >
                                                            {displayedDescription}
                                                        </div>
                                                    )}
                                                </TruncateWrapper>
                                                {shouldCollapseDescription ? (
                                                    <div
                                                        data-message-interactive="true"
                                                        style={{
                                                            marginTop: '6px',
                                                            cursor: 'pointer',
                                                            color: previewLinkTextColor,
                                                            fontSize: '12px',
                                                            fontWeight: 600,
                                                            alignSelf: 'flex-start'
                                                        }}
                                                        onMouseDown={handlePreviewToggleMouseDown}
                                                        onClick={event => handlePreviewToggleClick(event, setIsDescriptionExpanded)}
                                                    >
                                                        {isDescriptionExpanded ? lnT('messages:showLess') : lnT('messages:showMore')}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </UrlSection>
                                    ) : null}
                                </UrlDescriptionContent>
                                <UrlDescriptionActions $visible={showActionMenu || isEditable}>
                                    {renderSettingsButton()}
                                </UrlDescriptionActions>
                            </UrlDescriptionHeader>

                        </div>
                    </div>
                    <FooterBlock style={{ padding: '0 4px' }}>
                        {renderOpenOrRemove()}
                    </FooterBlock>
                    {metaData.quoted_url_meta ? (
                        <div style={{ padding: '0 4px', minWidth: 0 }}>
                            <UrlMetaQuotedPreview
                                metaData={metaData.quoted_url_meta}
                                allowImageRefresh={!readOnly}
                                expandable
                                mediaAboveText
                                onOpenWorkspace={onOpenVideoWorkspace ? source => onOpenVideoWorkspace(message, source) : undefined}
                                variant="dark"
                                onReadArticle={openArticleWorkspace}
                            />
                        </div>
                    ) : null}
                    {!readOnly && canGenerate ? <MediaToolsPanelContainer
                        ref={mediaToolsRef}
                        message={message}
                        metaData={metaData}
                        urlMetaIndex={index}
                        onVideoAnalysisStateChange={handleVideoAnalysisStateChange}
                        variant="dark"
                    /> : null}
                </PreviewSection>

                <CarouselModal
                readOnly={readOnly}
                allowExtraction={!readOnly && canGenerate}
                    isOpen={lightboxIsOpen}
                    closeModal={e => {
                        e.stopPropagation();
                        setLightboxIsOpen(false);
                    }}
                    items={carouselItems}
                    fileType="Link"
                    currentMessage={message}
                    messageId={message?.id}
                    initialIndex={effectiveGalleryIndex}
                    onExtractAllOcrCompleted={updatedMessage => {
                        const updatedMeta = updatedMessage?.urlMeta?.[index];
                        if (updatedMeta?.article_body) {
                            setExtractedCarouselArticleBody(String(updatedMeta.article_body));
                        }
                    }}
                    allOcrExtracted={galleryOcrIsComplete}
                ></CarouselModal>
                <ArticleReaderModal
                    isOpen={isArticleReaderOpen}
                    onRequestClose={() => setIsArticleReaderOpen(false)}
                    title={metaData?.title || message?.title}
                    url={metaData?.article_url || metaData?.url}
                    articleBody={articleBody}
                    redditComments={metaData?.reddit_comments}
                    sourcePlatform={metaData?.sourcePlatform}
                    sourceMetrics={metaData?.sourceMetrics}
                    sourcePlatformMeta={metaData?.sourcePlatformMeta}
                    onCreateAudio={
                        !readOnly && canGenerate && typeof onOpenArticleVoiceModal === 'function'
                            ? articleText => onOpenArticleVoiceModal(message, index, articleText)
                            : undefined
                    }
                    isCreateAudioDisabled={isGeneratingArticleAudio}
                    isCreatingAudio={isGeneratingArticleAudio}
                />
                <ArticleReaderModal
                    isOpen={Boolean(quotedArticleReaderMeta)}
                    onRequestClose={() => setQuotedArticleReaderMeta(null)}
                    title={quotedArticleReaderMeta?.title}
                    url={quotedArticleReaderMeta?.article_url || quotedArticleReaderMeta?.url}
                    articleBody={quotedArticleReaderMeta?.article_body || ''}
                    redditComments={quotedArticleReaderMeta?.reddit_comments}
                    sourcePlatform={quotedArticleReaderMeta?.sourcePlatform}
                    sourceMetrics={quotedArticleReaderMeta?.sourceMetrics}
                    sourcePlatformMeta={quotedArticleReaderMeta?.sourcePlatformMeta}
                />
            </>
        );
    }
    return null;
}

const TruncateWrapper = styled.div`
    flex: 1;
    color: rgba(255, 255, 255, 0.8);
    word-wrap: break-word;
    word-break: break-word;
    
    /* Ensure text color is set for all child elements */
    p {
        color: rgba(255, 255, 255, 0.8);
    }

    a {
        color: rgba(255, 255, 255, 0.92) !important;
    }
`;

const PreviewSection = styled.div`
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
`;

const UrlSection = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: 8px;
    min-width: 0; /* Allow flex item to shrink below content size */
    overflow: visible;
    padding-right: ${props => (props.$isEditable ? '28px' : '0')};
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const UrlDescriptionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    min-width: 0;
`;

const UrlDescriptionContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const UrlDescriptionActions = styled.div`
    flex: 0 0 auto;
    display: flex;
    justify-content: flex-end;
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 5;
    opacity: ${props => props.$visible ? 1 : 0};
    pointer-events: ${props => props.$visible ? 'auto' : 'none'};

    ${PreviewSection}:hover &, ${PreviewSection}:focus-within & {
        opacity: 1;
        pointer-events: auto;
    }
    @media (hover: none) { opacity: 1; pointer-events: auto; }
`;

const ActionMenuContainer = styled.div`
    position: relative;
`;

const ActionMenuTrigger = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 32px;
    min-width: 28px;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    padding: 0;
    transition: background-color 0.15s ease;

    &:hover {
        background: ${PALE_WHITE_1};
    }

    &:focus-visible {
        outline: 2px solid rgba(255, 255, 255, 0.4);
        outline-offset: 2px;
    }
`;

const EditCloseButton = styled(ActionMenuTrigger)`
    height: 28px;
    border: 1px solid ${PALE_WHITE_1};
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.09);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const ActionMenu = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 220px;
    padding: 8px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(18, 18, 20, 0.98);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
    z-index: 20;
`;

const ActionMenuItem = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 10px 12px;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.08);
    }

    &:disabled {
        cursor: default;
        opacity: 0.6;
    }
`;

const ActionMenuItemLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
`;

const FooterBlock = styled.div`
    margin-top: 8px;
`;

const FooterEditActions = styled.div`
    min-width: 0;
`;

const FooterPrimaryRow = styled.div`
    display: flex;
    align-items: center;
    min-height: 20px;
    min-width: 0;
`;

const FooterSecondaryRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    min-width: 0;
    flex-wrap: wrap;
`;

const FooterOpenLink = styled.a`
    color: ${FILE_BLUE};
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    flex-shrink: 0;
`;

const FooterReadButton = UrlPreviewReadButton;

const FooterActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 12px 14px;
    border-radius: 14px;
    border: 1px solid ${props => (
        props.$variant === 'danger'
            ? RED
            : PALE_WHITE_1
    )};
    background: ${props => (
        props.$variant === 'danger'
            ? 'transparent'
            : 'transparent'
    )};
    color: ${props => (props.$variant === 'danger' ? RED : 'rgba(255, 255, 255, 0.92)')};
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    flex: 1;
    min-width: 120px;
`;

const ImageUrlSection = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: ${props => props.isSmallScreen ? '0' : '4px'};
    overflow: visible;
    max-height: 300px;
    flex-shrink: 0;
    align-self: stretch;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    padding-right: ${props => (props.$isEditable ? '20px' : '0')};
`;

export default URLPreviewPlugin;
